import React from 'react';
import classNames from 'classnames';
import { DropzoneState } from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import { Button, SplitButton, SplitButtonMenuRenderFnOptions } from 'dig-components/buttons';
import { Menu } from 'dig-components/menu';
import * as types from 'hellospa/page/prep-and-send/data/types';
import dropboxIcon from 'images/v2/modules/home/integrations/dropbox_icon.svg';
import googleDriveIcon from 'images/v2/modules/home/integrations/drive_icon_@2x.png';
import boxIcon from 'images/v2/modules/home/integrations/box_icon_@2x.png';
import evernoteIcon from 'images/v2/modules/home/integrations/evernote_icon_@2x.png';
import oneDriveIcon from 'images/v2/modules/home/integrations/onedrive_icon_@2x.png';
import computerIcon from 'images/v2/common/computer.svg';
import uploadFileIcon from 'images/v2/common/upload-file.svg';
import templateIcon from 'images/v2/modules/home/template_icon_v2.svg';
import signGraphic from 'images/v2/components/prep-and-send/signgraphic.svg';
import loadingImage from 'images/v2/common/loader_@2x.gif';
import styles from '../styles.scss';

type Props = {
  allowUpload: boolean,
  allowTemplate: boolean,
  allowIntegrations: boolean|undefined,
  onClickAddTemplateButton: () => void,
  toggleIntegrationPicker: (integration: string, status: boolean) => void,
  integrations: any,
  dropzone: DropzoneState,
  isFirstTimeExperience?: boolean,
  isUploading: boolean,
  hideMenuLayoutSignerGraphic?: boolean,
}

const MenuLayout: React.FunctionComponent<Props> = ({
  allowUpload,
  allowTemplate,
  allowIntegrations,
  onClickAddTemplateButton,
  toggleIntegrationPicker,
  integrations,
  isFirstTimeExperience,
  dropzone: { getRootProps, getInputProps, open, isDragActive },
  isUploading,
  hideMenuLayoutSignerGraphic,
}) => {
  const uploadContainerClassName = classNames(
    styles.menuUploadContainer,
    styles.uploadContainerFullWidth,
    {
      [styles.uploadContainerBorder]: allowUpload,
    },
  );

  const dragAndDropContainerClassNames = classNames(
    styles.menuDragAndDropContainer,
    {
      [styles.dragActive]: isDragActive,
    },
  );

  const buttonsContainerClassName = classNames(
    styles.menuButtonsContainer,
    {
      [styles.menuButtonsContainerFullWidth]: !isFirstTimeExperience || hideMenuLayoutSignerGraphic,
      [styles.menuButtonsContainerPartialWidth]: isFirstTimeExperience
        && !hideMenuLayoutSignerGraphic,
    },
  );

  const renderUploadButtonMenu = ({ buttonProps }: SplitButtonMenuRenderFnOptions) => {
    return (
      <Menu.Wrapper>
        {({ getTriggerProps, getContentProps }) => (
          <>
            <Button
              aria-label="Show options"
              data-qa-ref="prep-integrations-menu"
              {...buttonProps}
              {...getTriggerProps()} />
            <Menu.Content
              {...getContentProps()}
              placement="bottom-end"
            >
              <Menu.Segment>
                <Menu.ActionItem data-qa-ref="prep-upload-files" onClick={open}>
                  <img
                    src={computerIcon}
                    className={styles.menuUploadContainerIcon} />
                  <FormattedMessage
                    id=""
                    description="Prep and send file dialog dropdown option"
                    defaultMessage="Computer" />
                </Menu.ActionItem>
                {allowTemplate && (
                  <Menu.ActionItem data-qa-ref="prep-add-template" onClick={onClickAddTemplateButton}>
                    <img
                      src={templateIcon}
                      className={styles.menuUploadContainerIcon} />
                    <FormattedMessage
                      id=""
                      description="Prep and send tempalte dialog dropdown option"
                      defaultMessage="Template" />
                  </Menu.ActionItem>
                )}
                {allowIntegrations && (
                  <>
                    {integrations && integrations[types.UploadIntegrationType.Dropbox] && (
                      <Menu.ActionItem
                        data-qa-ref="prep-dropbox-integration"
                        onClick={() => {
                          toggleIntegrationPicker(types.UploadIntegrationType.Dropbox, true);
                        }}
                      >
                        <img
                          src={dropboxIcon}
                          className={styles.menuUploadContainerIcon} />
                        <FormattedMessage
                          id=""
                          description="Prep and send integration dropdown option"
                          defaultMessage="Dropbox" />
                      </Menu.ActionItem>
                    )}
                    {integrations && integrations[types.UploadIntegrationType.GoogleDrive] && (
                      <Menu.ActionItem
                        data-qa-ref="prep-google-drive-integration"
                        onClick={() => {
                          toggleIntegrationPicker(types.UploadIntegrationType.GoogleDrive, true);
                        }}
                      >
                        <img
                          src={googleDriveIcon}
                          className={styles.menuUploadContainerIcon} />
                        <FormattedMessage
                          id=""
                          description="Prep and send integration dropdown option"
                          defaultMessage="Google Drive" />
                      </Menu.ActionItem>
                    )}
                    {integrations && integrations[types.UploadIntegrationType.Box] && (
                      <Menu.ActionItem
                        data-qa-ref="prep-box-integration"
                        onClick={() => {
                          toggleIntegrationPicker(types.UploadIntegrationType.Box, true);
                        }}
                      >
                        <img
                          src={boxIcon}
                          className={styles.menuUploadContainerIcon} />
                        <FormattedMessage
                          id=""
                          description="Prep and send integration dropdown option"
                          defaultMessage="Box" />
                      </Menu.ActionItem>
                    )}
                    {integrations && integrations[types.UploadIntegrationType.Evernote] && (
                      <Menu.ActionItem
                        data-qa-ref="prep-evernote-integration"
                        onClick={() => {
                          toggleIntegrationPicker(types.UploadIntegrationType.Evernote, true);
                        }}
                      >
                        <img
                          src={evernoteIcon}
                          className={styles.menuUploadContainerIcon} />
                        <FormattedMessage
                          id=""
                          description="Prep and send integration dropdown option"
                          defaultMessage="Evernote" />
                      </Menu.ActionItem>
                    )}
                    {integrations && integrations[types.UploadIntegrationType.OneDrive] && (
                      <Menu.ActionItem
                        data-qa-ref="prep-one-drive-integration"
                        onClick={() => {
                          toggleIntegrationPicker(types.UploadIntegrationType.OneDrive, true);
                        }}
                      >
                        <img
                          src={oneDriveIcon}
                          className={styles.menuUploadContainerIcon} />
                        <FormattedMessage
                          id=""
                          description="Prep and send integration dropdown option"
                          defaultMessage="OneDrive" />
                      </Menu.ActionItem>
                    )}
                  </>
                )}
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    );
  };

  const imageStyle = {
    margin: 'auto',
    display: 'block',
    width: '69.23px',
    padding: '0px',
  };

  const uploadButton = (
    <div>
      <input {...getInputProps()} data-qa-ref="upload-input" />
      <SplitButton
        data-qa-ref="prep-upload-files"
        variant={isFirstTimeExperience ? 'primary' : 'outline'}
        renderMenu={renderUploadButtonMenu}
        onClick={open}
      >
        {isUploading
          ? <div><img style={imageStyle} src={loadingImage} /></div>
          : <FormattedMessage
              id=""
              description="Prep and send file uploader button text"
              defaultMessage="Upload" />
        }

      </SplitButton>
    </div>
  );

  const dragAndDropContainer = allowUpload && (
    <div className={dragAndDropContainerClassNames}
      data-qa-ref="prep-drag-drop-container"
      // allow the drag & drop container to trigger the file picker
      onClick={open}
    >
      <img src={uploadFileIcon} />
      <div className={styles.menuDragAndDropText}>
        <FormattedMessage
          id=""
          description="text for prep and send to drag and drop files"
          defaultMessage="Drop files here to upload" />
      </div>
    </div>
  );

  return (
    <div
      {...getRootProps()}
      data-qa-ref="upload-container"
      className={uploadContainerClassName}
    >
      <div className={buttonsContainerClassName}>
        {dragAndDropContainer}
        {uploadButton}
      </div>
      {isFirstTimeExperience && !hideMenuLayoutSignerGraphic && (
        <div>
          <img
            src={signGraphic}
            className={styles.menuSignGraphic}
            data-qa-ref="first-time-signer-graphic" />
        </div>
      )}
    </div>
  );
};

export default MenuLayout;
